import { Box, Button, HStack, Text, Tooltip, VStack } from '@chakra-ui/react';
import { Icon } from '@blueprintjs/core';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import MapContext from '../../context/MapContext';

// Convert stored meters → units menu display units.
const DEPTH_UNIT_SCALE = {
    meters: 1,
    feet: 3.28084,
    fathoms: 1 / 1.8288,
};

const DEPTH_UNIT_SUFFIX = {
    meters: 'm',
    feet: 'ft',
    fathoms: 'fm',
};

function formatDepth(depthM, depthUnits = 'meters') {
    const n = Number(depthM);
    if (!Number.isFinite(n)) {
        return '';
    }
    const scale = DEPTH_UNIT_SCALE[depthUnits] || 1;
    const suffix = DEPTH_UNIT_SUFFIX[depthUnits] || 'm';
    const converted = n * scale;
    const rounded = Math.abs(converted - Math.round(converted)) < 0.05
        ? String(Math.round(converted))
        : converted.toFixed(1);
    return rounded + ' ' + suffix;
}

function DetailRow({ label, value, valueFontSize = '13px' }) {
    if (value == null || value === '') {
        return null;
    }
    return (
        <HStack spacing={2} align="baseline" justify="space-between" w="100%">
            <Text fontSize="13px" color="rgba(0,0,0,0.55)" flexShrink={0}>
                {label}
            </Text>
            <Text fontSize={valueFontSize} fontWeight="500" textAlign="right">
                {value}
            </Text>
        </HStack>
    );
}

export default function UserReportedBathymetryPopup({ popup, onClose }) {
    const { mapRef, depthUnits } = useContext(MapContext);
    const containerRef = useRef(null);
    const [position, setPosition] = useState(null);
    const [anchor, setAnchor] = useState(null);

    const depthLabel = formatDepth(popup?.depthM, depthUnits);
    const minDepthLabel = formatDepth(popup?.minDepthM, depthUnits);
    const maxDepthLabel = formatDepth(popup?.maxDepthM, depthUnits);

    useEffect(() => {
        if (!popup || !mapRef.current) {
            setAnchor(null);
            setPosition(null);
            return;
        }

        const map = mapRef.current;

        const updateAnchor = () => {
            if (!mapRef.current) {
                setAnchor(null);
                return;
            }
            const point = mapRef.current.project([popup.longitude, popup.latitude]);
            setAnchor({ x: point.x, y: point.y });
        };

        updateAnchor();
        map.on('move', updateAnchor);
        map.on('zoom', updateAnchor);
        map.on('rotate', updateAnchor);
        map.on('pitch', updateAnchor);
        map.on('resize', updateAnchor);

        return () => {
            map.off('move', updateAnchor);
            map.off('zoom', updateAnchor);
            map.off('rotate', updateAnchor);
            map.off('pitch', updateAnchor);
            map.off('resize', updateAnchor);
        };
    // Depend on coords explicitly so same-track re-clicks always re-anchor.
    }, [popup?.id, popup?.longitude, popup?.latitude, mapRef]);

    useLayoutEffect(() => {
        if (!popup || !anchor || !mapRef.current || !containerRef.current) {
            setPosition(null);
            return;
        }

        const mapEl = mapRef.current.getContainer();
        const popupEl = containerRef.current;
        const mapW = mapEl.clientWidth;
        const mapH = mapEl.clientHeight;
        const popupW = popupEl.offsetWidth;
        const popupH = popupEl.offsetHeight;

        // Prefer above the click point, centered; fall below if needed.
        let left = anchor.x - popupW / 2;
        let top = anchor.y - popupH - 14;
        if (top < 10) {
            top = anchor.y + 14;
        }

        left = Math.min(Math.max(left, 10), Math.max(10, mapW - popupW - 10));
        top = Math.min(Math.max(top, 10), Math.max(10, mapH - popupH - 10));

        setPosition({ left, top });
    }, [
        popup,
        anchor,
        mapRef,
        depthLabel,
        minDepthLabel,
        maxDepthLabel,
        popup?.lengthLabel,
        popup?.platformName,
    ]);

    if (!popup || !anchor) {
        return null;
    }

    const showFooter = !!popup.reportedAt;
    const hasDetails = !!(
        depthLabel
        || popup.lengthLabel
        || minDepthLabel
        || maxDepthLabel
        || popup.platformName
    );

    return (
        <Box
            ref={containerRef}
            pointerEvents="auto"
            zIndex={2}
            position="absolute"
            left={position ? `${position.left}px` : `${anchor.x}px`}
            top={position ? `${position.top}px` : `${anchor.y}px`}
            transform={position ? undefined : 'translate(-50%, calc(-100% - 14px))'}
            visibility={position ? 'visible' : 'hidden'}
            display="flex"
            flexDir="column"
            justifyContent="stretch"
            alignItems="stretch"
            bg="rgba(255,255,255,0.9)"
            borderRadius="4px"
            border="1px solid rgba(0,0,0,0.1)"
            shadow="md"
            padding="0px"
            overflow="hidden"
            boxShadow="0px 0px 0px 2px rgba(0,0,0,0.1)"
            minW="200px"
            userSelect="none"
        >
            <HStack spacing={2} p="10px" pr="44px" align="center">
                <Icon icon="timeline-line-chart" size={16} style={{ opacity: 0.5, flexShrink: 0 }} />
                <Text fontWeight="500">Reported Bathymetry</Text>
            </HStack>
            {hasDetails && (
                <VStack
                    spacing={1}
                    align="stretch"
                    w="100%"
                    bg="rgba(0,0,0,0.05)"
                    borderTop="1px solid rgba(0,0,0,0.1)"
                    px="10px"
                    py="8px"
                >
                    <DetailRow label="Depth at Point" value={depthLabel} valueFontSize="18px" />
                    {depthLabel ? (
                        <Box
                            as="hr"
                            border="0"
                            borderTop="1px solid rgba(0,0,0,0.12)"
                            my="4px"
                            w="100%"
                        />
                    ) : null}
                    <DetailRow label="Track Length" value={popup.lengthLabel} />
                    <DetailRow label="Min depth" value={minDepthLabel} />
                    <DetailRow label="Max depth" value={maxDepthLabel} />
                    <DetailRow label="Platform" value={popup.platformName} />
                </VStack>
            )}
            {showFooter && (
                <HStack
                    spacing={1}
                    bg="rgba(0,0,0,0.05)"
                    w="100%"
                    h="40px"
                    minH="40px"
                    borderTop="1px solid rgba(0,0,0,0.1)"
                    pl="10px"
                    pr="10px"
                    py="0"
                    align="center"
                >
                    <Text fontSize="13px" flex="1" minW={0} noOfLines={1}>
                        {`Reported ${popup.reportedAt}`}
                    </Text>
                </HStack>
            )}
            <Tooltip placement="right" label={<Text>Close</Text>}>
                <Button
                    _hover={{
                        color: 'rgba(0,0,0,1.0)',
                    }}
                    bg={''}
                    w="40px"
                    h="40px"
                    position="absolute"
                    top="0px"
                    right="0px"
                    onClick={onClose}
                    color="rgba(0,0,0,0.8)"
                >
                    <Text mb="0px">
                        <Icon icon="cross" size="16" />
                    </Text>
                </Button>
            </Tooltip>
        </Box>
    );
}
